import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import ReactSelect, { components } from 'react-select';
import Icon from '../Icon';
import './Select.css';
import { useTheme } from "../../../src/contextProviders"

const MultiValue = props => {
  const values = props.selectProps.value;
  const lastValue = values[values.length - 1];
  let label = props.data.label;
  if (lastValue.label !== label) {
    label += ', ';
  }
  return <p>{label}</p>;
};

const Option = props => {
  return (
    <components.Option {...props}>
      <div className="flex items-center">
        <div className="h-2 w-2">
          {props.isSelected ? (
            <Icon name={'checkbox-active'} />
          ) : (
            <Icon name={'checkbox-default'} />
          )}
        </div>
        <label id={props.data.value} className="ml-3 mt-1">
          <span>{props.value}</span>
        </label>
      </div>
    </components.Option>
  );
};

const Select = ({
  id,
  className = '',
  closeMenuOnSelect = true,
  hideSelectedOptions = false,
  isClearable = true,
  isDisabled = false,
  isMulti = false,
  isSearchable = true,
  onChange,
  options,
  placeholder,
  noIcons = false,
  menuPlacement = 'auto',
  components = {},
  value = [],
}) => {
  const _noIconComponents = {
    DropdownIndicator: () => null,
    IndicatorSeparator: () => null,
  };
  let _components = isMulti ? { Option, MultiValue } : {};
  _components = noIcons
    ? { ..._components, ..._noIconComponents }
    : { ..._components, ...components };

  const selectedOptions = [];

  if (value && Array.isArray(value)) {
    value.forEach(val => {
      const found = options.find(opt => opt.value === val);
      if (found) {
        selectedOptions.push(JSON.parse(JSON.stringify(found)));
      }
    });
  }
  const isDarkMode = useTheme();

  return (
    <ReactSelect
      inputId={`input-${id}`}
      className={classnames(className, 'ohif-select customSelect__wrapper flex flex-1 flex-col')}
      data-cy={`input-${id}`}
      classNamePrefix={`${isDarkMode?.isDarkMode ? 'drop-class-light' : ''} customSelect field`}
      isDisabled={isDisabled}
      isClearable={isClearable}
      isMulti={isMulti}
      isSearchable={isSearchable}
      menuPlacement={menuPlacement}
      closeMenuOnSelect={isMulti ? false : true}
      hideSelectedOptions={hideSelectedOptions}
      components={_components}
      placeholder={placeholder}
      options={options}
      blurInputOnSelect={false}
      menuPortalTarget={document.body}
      styles={{
        menuPortal: base => ({ ...base, zIndex: 9999 }),
        control: (styles, { isFocused }) => ({
          ...styles,
          backgroundColor: 'form__select form__select--sm',
          borderColor: isFocused ? '#000' : '#CBCAD7',
          boxShadow: isFocused ? '0 0 0 1px #000' : null,
          borderRadius: '2rem',
          minHeight: '44px',
          '&:hover': {
            borderColor: '#CBCAD7',
          },
          cursor: 'pointer',
        }),
        option: (styles, { isFocused, isSelected }) => ({
          ...styles,
          backgroundColor: isSelected
            ? '#0F8491'
            : isFocused
              ? '#f0f0f0' // Custom hover background
              : undefined,
          color: isSelected ? 'white' : 'black',
          '&:hover': {
            backgroundColor: '#dcdcdc', // Hover color
          },
        }),
        menu: styles => ({
          ...styles,
          backgroundColor: '#ffffff', // Dropdown background color
        }),
        singleValue: styles => ({
          ...styles,
          color: 'black', // Text color
        }),
        multiValue: styles => ({
          ...styles,
          backgroundColor: 'form__select form__select--sm', // Multi-select value background color
        }),
        multiValueLabel: styles => ({
          ...styles,
          color: 'black', // Multi-select value label color
        }),
        multiValueRemove: styles => ({
          ...styles,
          color: 'black',
          ':hover': {
            backgroundColor: 'form__select form__select--sm',
            color: 'white',
          },
        }),
        placeholder: (styles) => ({
          ...styles,
          color: 'black',
          fontSize: '14px',
        }),
      }}
      value={value && Array.isArray(value) ? selectedOptions : value}
      onChange={(selectedOptions, { action }) => {
        const newSelection = !selectedOptions?.length
          ? selectedOptions
          : selectedOptions?.reduce((acc, curr) => acc.concat([curr.value]), []);
        onChange(newSelection, action);
      }}
    />
  );
};

Select.propTypes = {
  className: PropTypes.string,
  closeMenuOnSelect: PropTypes.bool,
  hideSelectedOptions: PropTypes.bool,
  isClearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isMulti: PropTypes.bool,
  isSearchable: PropTypes.bool,
  noIcons: PropTypes.bool,
  menuPlacement: PropTypes.oneOf(['auto', 'bottom', 'top']),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })
  ),
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.any]),
};

export default Select;
